import React, { useState, useEffect, lazy, Suspense } from 'react';
import './App.css';
import Header from './commoncomponents/Header/Header';
import Footer from './commoncomponents/Footer/Footer';
import Banner from './components/Banner/Banner';

// Lazy load below-the-fold components
const AboutLoanProgram = lazy(() => import('./components/AboutLoanProgram/index'));
const WhyChooseLoanProgram = lazy(() => import('./components/WhyChooseLoanProgram/index'));
const LoanMortgageInsurance = lazy(() => import('./components/LoanMortgageInsurance/loanmortgageinsurance'));
const HowLoanProgramWorks = lazy(() => import('./components/howLoanProgramWorks/howLoanProgramWorks'));
const LoanJumboSection = lazy(() => import('./components/LoanJumboSection/loanjumbosection'));
const MythsAndFacts = lazy(() => import('./components/MythsAndFacts/mythsandfacts'));
const IncreasingLoanLimit = lazy(() => import('./components/IncreasingLoanLimit/increasingloanlimit'));
const LoanFinancialAssessment = lazy(() => import('./components/LoanFinancialAssessment/loanfinancialassessment'));
const LoanClosingTimeline = lazy(() => import('./components/LoanClosingTimeline/loanclosingtimeline'));
const ApplyForLoanWithSWMC = lazy(() => import('./components/ApplyForLoanWithSWMC/index'));
const FAQ = lazy(() => import('./components/FAQ/index'));
const Disclaimer = lazy(() => import('./components/Disclaimer/Disclaimer'));



// Loading component for lazy-loaded components
const LoadingComponent = () => (
  <div className="component-loading">
    <div className="loading-spinner"></div>
  </div>
);

function Home({ data }) {
  const [homePageData, setHomePageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOtherComponents, setShowOtherComponents] = useState(false);

  const handleShowOtherComponents =  () => setShowOtherComponents(true);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.body.offsetHeight;
      const scrollPosition =
        window.scrollY ||
        window.pageYOffset ||
        document.documentElement.scrollTop;
      const maxScroll = documentHeight - windowHeight;
      const percentage = (scrollPosition / maxScroll) * 100;

      if (percentage > 1) {
        handleShowOtherComponents();
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch the reverse.json file
        const reversePageResponse = await fetch(`${process.env.REACT_APP_CDN_URL}loan-programs/includes/reverse.json`);
        
        if (!reversePageResponse.ok) {
          throw new Error(`HTTP error! Status: ${reversePageResponse.status}`);
        }
       
        const reversePageResult = await reversePageResponse.json();
        
        setHomePageData(reversePageResult);
        setLoading(false);
        console.log("Reverse page data loaded:", reversePageResult);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  if (loading) {
    return <div className="loading">Loading...</div>;
  }
 
  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!homePageData) {
    return null;
  }

  return (
    <div className="home-page-container">
      {/* Header - Always rendered first */}
      {homePageData.header && <Header data={homePageData.header} />}
      
      {/* Banner - Critical above-the-fold content */}
      {homePageData.banner && <Banner data={homePageData.banner} />}
      
      {/* About Loan Program */}
      {homePageData.aboutLoanProgram && (
        <Suspense fallback={<LoadingComponent />}>
          <AboutLoanProgram data={homePageData.aboutLoanProgram} />
        </Suspense>
      )}
      
      {/* Why Choose Loan Program */}
      {homePageData.whyChooseLoanProgram && (
        <Suspense fallback={<LoadingComponent />}>
          <WhyChooseLoanProgram data={homePageData.whyChooseLoanProgram} />
        </Suspense>
      )}
      
      {/* Loan Mortgage Insurance */}
      {homePageData.loanMortgageInsurance && (
        <Suspense fallback={<LoadingComponent />}>
          <LoanMortgageInsurance data={homePageData.loanMortgageInsurance} />
        </Suspense>
      )}
       {showOtherComponents ? (
       <>
      {/* How Loan Program Works */}
      {homePageData.howLoanProgramWorks && (
        <Suspense fallback={<LoadingComponent />}>
          <HowLoanProgramWorks data={homePageData.howLoanProgramWorks} />
        </Suspense>
      )}
      
      {/* How Loan Program Works 1 */}
      {homePageData.howLoanProgramWorks1 && (
        <Suspense fallback={<LoadingComponent />}>
          <HowLoanProgramWorks data={homePageData.howLoanProgramWorks1} />
        </Suspense>
      )}
      
      {/* Loan Jumbo Section */}
      {homePageData.loanjumbosection && (
        <Suspense fallback={<LoadingComponent />}>
          <LoanJumboSection data={homePageData.loanjumbosection} />
        </Suspense>
      )}
      
      {/* Myths and Facts */}
      {homePageData.mythsandfacts && (
        <Suspense fallback={<LoadingComponent />}>
          <MythsAndFacts data={homePageData.mythsandfacts} />
        </Suspense>
      )}
      
      {/* Increasing Loan Limit */}
      {homePageData.increasingloanlimit && (
        <Suspense fallback={<LoadingComponent />}>
          <IncreasingLoanLimit data={homePageData.increasingloanlimit} />
        </Suspense>
      )}
      
      {/* Loan Financial Assessment */}
      {homePageData.loanfinancialassessment && (
        <Suspense fallback={<LoadingComponent />}>
          <LoanFinancialAssessment data={homePageData.loanfinancialassessment} />
        </Suspense>
      )}
      
      {/* Loan Closing Timeline */}
      {homePageData.loanClosingTimeline && (
        <Suspense fallback={<LoadingComponent />}>
          <LoanClosingTimeline data={homePageData.loanClosingTimeline} />
        </Suspense>
      )}
      
      {/* Apply For Loan With SWMC */}
      {homePageData.applyforloanwithSWMC && (
        <Suspense fallback={<LoadingComponent />}>
          <ApplyForLoanWithSWMC data={homePageData.applyforloanwithSWMC} />
        </Suspense>
      )}
      
      {/* FAQ Section */}
      {homePageData.faqSection && (
        <Suspense fallback={<LoadingComponent />}>
          <FAQ data={homePageData.faqSection} />
        </Suspense>
      )}
      
      {/* Disclaimer Section */}
      {homePageData.disclaimerSection && (
        <Suspense fallback={<LoadingComponent />}>
          <Disclaimer data={homePageData.disclaimerSection} />
        </Suspense>
      )}
      
      {/* Footer - Always rendered last */}
      <Footer />
      </>
        ) : (
          ""
        )}
    </div>
  );
}

export default Home;